import { useEffect, useState } from "react";
import type {
  InsuranceNetwork,
  TherapeuticChoiceCombination,
  TherapeuticChoiceRowKey,
  TherapeuticChoicesFeesSettings,
} from "cadendar-shared";
import {
  applyDefaultRowToEmptyNetworkRows,
  createEmptyTherapeuticChoicesFeesSettings,
  insuranceNetworkLabels,
  insuranceNetworkValues,
  normalizeTherapeuticChoiceRowSettings,
  normalizeTherapeuticChoicesFeesSettings,
} from "cadendar-shared";
import Button from "../../main/components/Button";
import { trpc } from "../../main/components/trpc.ts";
import TherapeuticChoiceEditorModal from "./TherapeuticChoiceEditorModal";
import TherapeuticChoicesTable from "./TherapeuticChoicesTable";
import { useTherapeuticChoicesFees } from "../hooks/useTherapeuticChoicesFees";

interface EditingState {
  network: InsuranceNetwork | null;
  rowKey: TherapeuticChoiceRowKey;
  choiceIndex: number | null;
}

function getActiveNetworks(prefs: {
  insurance_networks?: Partial<Record<InsuranceNetwork, boolean>>;
} | null | undefined) {
  return insuranceNetworkValues.filter(
    (network) => prefs?.insurance_networks?.[network] === true,
  );
}

const TherapeuticChoicesFeesPage = () => {
  const therapeuticChoicesFees = useTherapeuticChoicesFees();
  const prefsQuery = trpc.preference.get.useQuery();
  const activeNetworks = getActiveNetworks(prefsQuery.data);
  const [draft, setDraft] = useState<TherapeuticChoicesFeesSettings | null>(null);
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (therapeuticChoicesFees.settings) {
      setDraft(normalizeTherapeuticChoicesFeesSettings(therapeuticChoicesFees.settings));
      setSaveFeedback(null);
    }
  }, [therapeuticChoicesFees.settings]);

  const resolvedDraft = draft ?? therapeuticChoicesFees.settings ?? createEmptyTherapeuticChoicesFeesSettings();
  const baselineSettings = normalizeTherapeuticChoicesFeesSettings(
    therapeuticChoicesFees.settings,
  );
  const isDirty =
    JSON.stringify(resolvedDraft) !== JSON.stringify(baselineSettings);

  const getRowSettingsForNetwork = (network: InsuranceNetwork | null) => {
    if (network === null) {
      return resolvedDraft.defaultRows;
    }

    return normalizeTherapeuticChoiceRowSettings(resolvedDraft.networkRows[network]);
  };

  const getCurrentChoice = () => {
    if (!editingState) {
      return null;
    }

    const row = getRowSettingsForNetwork(editingState.network)[editingState.rowKey];
    if (editingState.choiceIndex === null) {
      return null;
    }

    return row[editingState.choiceIndex] ?? null;
  };

  const updateRow = (options: {
    network: InsuranceNetwork | null;
    rowKey: TherapeuticChoiceRowKey;
    updater: (row: TherapeuticChoiceCombination[]) => TherapeuticChoiceCombination[];
  }) => {
    setDraft((currentDraft) => {
      const base = normalizeTherapeuticChoicesFeesSettings(
        currentDraft ?? resolvedDraft,
      );

      if (options.network === null) {
        const nextSettings = {
          ...base,
          defaultRows: {
            ...base.defaultRows,
            [options.rowKey]: options.updater(base.defaultRows[options.rowKey]),
          },
        };

        return applyDefaultRowToEmptyNetworkRows({
          settings: nextSettings,
          activeNetworks,
          rowKey: options.rowKey,
        });
      }

      const networkRows = normalizeTherapeuticChoiceRowSettings(
        base.networkRows[options.network],
      );

      return {
        ...base,
        networkRows: {
          ...base.networkRows,
          [options.network]: {
            ...networkRows,
            [options.rowKey]: options.updater(networkRows[options.rowKey]),
          },
        },
      };
    });
  };

  const handleSaveChoice = (choice: TherapeuticChoiceCombination) => {
    if (!editingState) {
      return;
    }

    updateRow({
      network: editingState.network,
      rowKey: editingState.rowKey,
      updater: (row) => {
        if (editingState.choiceIndex === null) {
          return [...row, choice];
        }

        return row.map((existingChoice, index) =>
          index === editingState.choiceIndex ? choice : existingChoice,
        );
      },
    });
    setEditingState(null);
  };

  const handleDeleteChoice = (options: {
    network: InsuranceNetwork | null;
    rowKey: TherapeuticChoiceRowKey;
    choiceIndex: number;
  }) => {
    updateRow({
      network: options.network,
      rowKey: options.rowKey,
      updater: (row) => row.filter((_, index) => index !== options.choiceIndex),
    });
  };

  const handleSaveSettings = async () => {
    const saved = await therapeuticChoicesFees.saveSettings(resolvedDraft);
    setDraft(saved);
    setSaveFeedback("Réglages enregistrés.");
  };

  if (therapeuticChoicesFees.isLoading && draft === null) {
    return <div className="p-6 text-slate-600">Chargement des réglages...</div>;
  }

  if (therapeuticChoicesFees.error) {
    return (
      <div className="p-6 text-red-700">
        Impossible de charger les réglages : {therapeuticChoicesFees.error.message}
      </div>
    );
  }

  return (
    <div className="bg-slate-100 p-4">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-slate-950">
                Choix thérapeutiques et honoraires
              </h1>
              <p className="mt-2 max-w-4xl text-sm text-slate-600">
                Le tableau Défaut alimente automatiquement les lignes réseau encore vides.
                Les matériaux sont filtrés selon le réseau et la nature de chaque acte.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                title="Réinitialiser"
                onClick={() => {
                  setDraft(baselineSettings);
                  setSaveFeedback(null);
                }}
                disabled={!isDirty || therapeuticChoicesFees.isSaving}
              />
              <Button
                title={therapeuticChoicesFees.isSaving ? "Enregistrement..." : "Enregistrer"}
                onClick={handleSaveSettings}
                style="main"
                disabled={!isDirty || therapeuticChoicesFees.isSaving}
              />
            </div>
          </div>

          {saveFeedback ? (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {saveFeedback}
            </div>
          ) : null}

          {therapeuticChoicesFees.saveError ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {therapeuticChoicesFees.saveError.message}
            </div>
          ) : null}
        </section>

        <TherapeuticChoicesTable
          title="Défaut"
          network={null}
          rowSettings={resolvedDraft.defaultRows}
          onAddChoice={(rowKey) => {
            setEditingState({ network: null, rowKey, choiceIndex: null });
          }}
          onEditChoice={(rowKey, index) => {
            setEditingState({ network: null, rowKey, choiceIndex: index });
          }}
          onDeleteChoice={(rowKey, choiceIndex) => {
            handleDeleteChoice({
              network: null,
              rowKey,
              choiceIndex,
            });
          }}
        />

        {activeNetworks.length === 0 ? (
          <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            Aucun réseau actif dans les préférences du praticien.
          </section>
        ) : (
          activeNetworks.map((network) => (
            <TherapeuticChoicesTable
              key={network}
              title={insuranceNetworkLabels[network]}
              network={network}
              rowSettings={getRowSettingsForNetwork(network)}
              onAddChoice={(rowKey) => {
                setEditingState({ network, rowKey, choiceIndex: null });
              }}
              onEditChoice={(rowKey, index) => {
                setEditingState({ network, rowKey, choiceIndex: index });
              }}
              onDeleteChoice={(rowKey, choiceIndex) => {
                handleDeleteChoice({
                  network,
                  rowKey,
                  choiceIndex,
                });
              }}
            />
          ))
        )}
      </div>

      {editingState ? (
        <TherapeuticChoiceEditorModal
          rowKey={editingState.rowKey}
          network={editingState.network}
          initialChoice={getCurrentChoice()}
          onClose={() => setEditingState(null)}
          onSave={handleSaveChoice}
        />
      ) : null}
    </div>
  );
};

export default TherapeuticChoicesFeesPage;
